import { ApiResult } from './api-result';
import { createAbortError, mapWithConcurrency, withRetry } from './async-pool';

export type Page<T> = { data: T[]; total: number };
export type PagedResult<T> = ApiResult<200, Page<T>> | ApiResult<400 | 500, string>;
export type PagedFetcher<T> = (query: Record<string, any>, signal?: AbortSignal) => Promise<PagedResult<T>>;

export interface FetchAllPagesOptions {
  perPage?: number;
  concurrency?: number;
  retries?: number;
  retryDelayMs?: number;
  signal?: AbortSignal;
  onProgress?: (loadedPages: number, totalPages: number) => void;
}

export class PageFetchError extends Error {
  constructor(
    public readonly page: number,
    message: string,
  ) {
    super(message);
    this.name = 'PageFetchError';
    Object.setPrototypeOf(this, PageFetchError.prototype);
  }
}

export class IncompleteFetchError extends Error {
  constructor(
    public readonly failedPages: number[],
    public readonly totalPages: number,
  ) {
    super(`${failedPages.length} of ${totalPages} pages could not be loaded`);
    this.name = 'IncompleteFetchError';
    Object.setPrototypeOf(this, IncompleteFetchError.prototype);
  }
}

export const fetchAllPages = async <T>(fetcher: PagedFetcher<T>, query: Record<string, any>, options: FetchAllPagesOptions = {}): Promise<T[]> => {
  const { perPage = 500, concurrency = 4, retries = 2, retryDelayMs = 1000, signal, onProgress } = options;

  const fetchPage = (page: number) =>
    withRetry(
      async () => {
        const result = await fetcher({ ...query, page, per_page: perPage }, signal);
        if (signal?.aborted) throw createAbortError();
        if (result.status !== 200) throw new PageFetchError(page, result.data as string);
        return (result as ApiResult<200, Page<T>>).data;
      },
      { retries, baseDelayMs: retryDelayMs, signal },
    );

  const firstPage = await fetchPage(1);
  const totalPages = Math.max(1, Math.ceil(firstPage.total / perPage));
  let loadedPages = 1;
  onProgress?.(loadedPages, totalPages);

  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
  const results = await mapWithConcurrency(
    remainingPages,
    async (page) => {
      const { data } = await fetchPage(page);
      onProgress?.(++loadedPages, totalPages);
      return data;
    },
    { concurrency, signal },
  );

  const failedPages = remainingPages.filter((_, i) => results[i].status === 'rejected');
  if (failedPages.length) throw new IncompleteFetchError(failedPages, totalPages);

  return [...firstPage.data, ...results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []))];
};
